import {
  normalizeNotificationPreference,
  validateNotificationPayload,
  type NotificationKind,
  type NotificationPreference,
  type PushStatus,
} from '../../domain/notification'
import type { LocalEntity } from '../../domain/sync'
import type { CaravanDb, LocalNotificationRecord } from './db'
import { assertTripWritable, LocalRepository } from './repository'
import { CARAVAN_TRIP } from '../../config/trip'

function preferenceFromEntity(entity: LocalEntity): NotificationPreference {
  const pushStatus = typeof entity.payload.pushStatus === 'string' ? entity.payload.pushStatus : 'disabled'
  const allowedStatuses: PushStatus[] = ['disabled', 'default', 'granted', 'denied', 'unavailable', 'requires_install', 'configuration_missing']
  return normalizeNotificationPreference({
    socialEnabled: entity.payload.socialEnabled !== false,
    pushStatus: allowedStatuses.includes(pushStatus as PushStatus) ? pushStatus as PushStatus : 'disabled',
  })
}

export class NotificationRepository {
  readonly #db: CaravanDb
  readonly #local: LocalRepository

  constructor(db: CaravanDb) {
    this.#db = db
    this.#local = new LocalRepository(db)
  }

  async getPreference(tripId: string, memberId: string): Promise<NotificationPreference> {
    const key = `notification_preference:${memberId}`
    let entity = await this.#db.entities.get(key)
    if (!entity) {
      const seededAt = `${CARAVAN_TRIP.startsOn}T00:00:00.000Z`
      entity = {
        key, id: memberId, tripId, entityType: 'notification_preference',
        payload: { socialEnabled: true, pushStatus: 'disabled', updatedAt: seededAt },
        createdAtDevice: seededAt, updatedAtDevice: seededAt, updatedAtServer: seededAt,
        version: 1, deletedAt: null, lastMutationId: `seed:notification:${memberId}`,
        syncState: 'synced', lastError: null,
      }
      await this.#db.entities.put(entity)
    }
    return preferenceFromEntity(entity)
  }

  async #savePreference(tripId: string, memberId: string, change: Partial<{ socialEnabled: boolean; pushStatus: PushStatus }>): Promise<NotificationPreference> {
    await assertTripWritable(this.#db, tripId)
    const current = await this.getPreference(tripId, memberId)
    const updatedAt = new Date().toISOString()
    const payload = {
      socialEnabled: change.socialEnabled ?? current.socialEnabled,
      pushStatus: change.pushStatus ?? current.pushStatus,
      updatedAt,
    }
    const key = `notification_preference:${memberId}`
    const entity = await this.#db.entities.get(key)
    if (!entity) throw new Error('Préférence de notification introuvable.')
    const pending = await this.#db.outbox
      .where('entityType')
      .equals('notification_preference')
      .filter((mutation) => mutation.entityId === memberId && mutation.state === 'pending')
      .first()
    if (pending) {
      await this.#db.transaction('rw', this.#db.entities, this.#db.outbox, async () => {
        await this.#db.entities.update(key, { payload, updatedAtDevice: updatedAt, syncState: 'pending' })
        await this.#db.outbox.update(pending.mutationId, { payload, createdAtDevice: updatedAt })
      })
    } else {
      await this.#local.mutate({
        mutationId: crypto.randomUUID(), entityType: 'notification_preference', entityId: memberId,
        tripId, operation: 'upsert', baseVersion: entity.version, payload, createdAtDevice: updatedAt,
      })
    }
    return normalizeNotificationPreference({ socialEnabled: payload.socialEnabled, pushStatus: payload.pushStatus })
  }

  setSocialEnabled(tripId: string, memberId: string, enabled: boolean): Promise<NotificationPreference> {
    return this.#savePreference(tripId, memberId, { socialEnabled: enabled })
  }

  setPushStatus(tripId: string, memberId: string, pushStatus: PushStatus): Promise<NotificationPreference> {
    return this.#savePreference(tripId, memberId, { pushStatus })
  }

  async createInApp(input: {
    id?: string
    tripId: string
    memberId: string
    kind: NotificationKind
    importance: 'important' | 'social'
    title: string
    body: string
    deepLink: string
    createdAt?: string
  }): Promise<LocalNotificationRecord> {
    const payload = validateNotificationPayload({
      id: input.id ?? crypto.randomUUID(), kind: input.kind, importance: input.importance,
      title: input.title, body: input.body, deepLink: input.deepLink,
    })
    const record: LocalNotificationRecord = {
      ...payload,
      tripId: input.tripId,
      memberId: input.memberId,
      createdAt: input.createdAt ?? new Date().toISOString(),
      readAt: null,
      deliveryState: 'in_app',
    }
    await this.#db.notifications.put(record)
    return record
  }

  async ensurePendingSyncAlert(tripId: string, memberId: string, pendingSince: number | null): Promise<void> {
    if (pendingSince === null || Date.now() - pendingSince < 15 * 60_000) return
    const id = `sync-pending:${memberId}`
    const existing = await this.#db.notifications.get(id)
    if (existing?.readAt === null) return
    await this.createInApp({
      id, tripId, memberId, kind: 'sync_pending', importance: 'important',
      title: 'Données encore en attente',
      body: 'Ouvrez le centre de synchronisation pour réessayer dès que le réseau revient.',
      deepLink: '/',
    })
  }

  async listVisible(memberId: string): Promise<LocalNotificationRecord[]> {
    const rows = await this.#db.notifications.where('memberId').equals(memberId).toArray()
    const tripId = rows[0]?.tripId
    const preference = tripId ? await this.getPreference(tripId, memberId) : normalizeNotificationPreference({ socialEnabled: true, pushStatus: 'disabled' })
    return rows
      .filter((item) => item.importance === 'important' || preference.socialEnabled)
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
  }

  async markRead(id: string, memberId: string): Promise<void> {
    const record = await this.#db.notifications.get(id)
    if (record?.memberId !== memberId) throw new Error('Notification inaccessible.')
    const readAt = record.readAt ?? new Date().toISOString()
    await this.#db.notifications.update(id, { readAt })
    if (record.readAt === null && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(id)) {
      const existing = await this.#db.entities.get(`notification:${id}`)
      await this.#local.mutate({
        mutationId: crypto.randomUUID(), entityType: 'notification', entityId: id,
        tripId: record.tripId, operation: 'update', baseVersion: existing?.version ?? 0,
        payload: { readAt }, createdAtDevice: readAt,
      })
    }
  }
}
